import React, { useEffect, useState } from "react"
import { Col, Row } from "reactstrap"
import toast from "react-hot-toast"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"
import cloneDeep from "lodash/cloneDeep"

import { Modal } from "components/Modal"
import { AddProductionStages, GetDepartmentsListing } from "api/api.service"
import { DataTable } from "components/DataTable"
import { AutoComplete } from "components/AutoComplete"
import DeleteModal from "pages/Configurator/CommonUI/Modal/DeleteModal"
import useMultiSelect from "hooks/useMultiSelect"
import useAsyncSelect from "hooks/useAsyncSelect"
import { TextInput } from "components/TextInput"
import { AutoCompleteStyling, stageHoursSelectOptions } from "./FormConstants"
import { normalizeStageData } from "pages/ProductionLineManagement/ProductionLineDetails_Stages"
import { Button } from "components/Button"

import EditIcon from "../../../../assets/images/icons/edit-icon.svg"
import DeleteIcon from "../../../../assets/images/icons/finance-vendor-mang-del-icon.svg"
import reorderIcon from "../../../../assets/images/reorder.svg"
import { isEmpty } from "utils/commonUtils"

const COLUMNS = {
   REORDER: "Reorder",
   STAGES: "Stages",
   STATIONS: "Stations",
   TIME: "Time (Hrs)",
   ACTIONS: "Actions",
}

const MODAL_TYPE = {
   EDIT: "EDIT",
   DELETE: "DELETE",
}

const formatStationsData = data => {
   return (
      data?.stations
         ?.map(station => station?.department?.departmentName)
         ?.join(", ") || "-"
   )
}

const EditStageModal = props => {
   const {
      isOpen = false,
      handleModalToggling = () => {},
      stagesData = [],
      onSuccess = () => {},
      productionLineId = null,
   } = props

   let controller = new AbortController()
   let signal = controller.signal

   const [isLoading, setIsLoading] = useState(false)
   const [isDeleting, setIsDeleting] = useState(false)
   const [modal, setModal] = useState({
      isOpen: false,
      type: MODAL_TYPE.DELETE,
   })

   const [localStagesData, setLocalStagesData] = useState([])
   const [editedStages, setEditedStages] = useState({})
   const [selectedStageId, setSelection] = useState(null)

   const {
      toggleItem: toggleStages,
      hasSelectedItem: hasSelectedStages,
      clearSelection: clearSelectedStages,
      selectedItems: selectedStages,
   } = useMultiSelect()

   const {
      selectEntity: departmentListing,
      handleGetSelectEntityData,
      handleSearchOnOptions,
      setSelectEntity: setStationsOption,
   } = useAsyncSelect({
      apiFunc: GetDepartmentsListing,
      labelKey: "departmentName",
      valueKey: "id",
      dataKey: "data",
      defaultPage: 1,
   })

   useEffect(() => {
      setLocalStagesData(stagesData)
   }, [stagesData])

   useEffect(() => {
      return () => {
         controller.abort()
      }
   }, [])

   const handleDragEnd = result => {
      const { source, destination } = result
      if (!destination || source.index === destination.index) return
      const updatedStages = cloneDeep(localStagesData)
      const [movedStage] = updatedStages.splice(source.index, 1)
      updatedStages.splice(destination.index, 0, movedStage)
      setLocalStagesData(updatedStages)
   }

   const handleModalClose = () => {
      clearSelectedStages()
      setEditedStages({})
      setLocalStagesData(stagesData)
      handleModalToggling(prev => !prev)
   }

   const handleStageNameChange = (stageId, value) => {
      setEditedStages(prev => ({
         ...prev,
         [stageId]: {
            ...prev[stageId],
            name: value,
         },
      }))
   }

   const handleStationsChange = (stageId, selectedOptions) => {
      setEditedStages(prev => ({
         ...prev,
         [stageId]: {
            ...prev[stageId],
            stations: selectedOptions || [],
         },
      }))
   }

   const handleHoursChange = (stageId, selectedOption) => {
      setEditedStages(prev => ({
         ...prev,
         [stageId]: {
            ...prev[stageId],
            hours: selectedOption,
         },
      }))
   }

   const handleSaveStage = stageId => {
      if (!editedStages[stageId]) return

      if (!editedStages[stageId].name) {
         toast.error("Please enter a stage name")
         return
      }

      if (!editedStages[stageId].stations?.length) {
         toast.error("Please select at least one station")
         return
      }

      const editedStage = editedStages[stageId]
      if (editedStage) {
         const updatedStagesData = cloneDeep(localStagesData)
         const stageIndex = updatedStagesData.findIndex(
            stage => stage.id === stageId
         )
         if (stageIndex !== -1) {
            updatedStagesData[stageIndex] = {
               ...updatedStagesData[stageIndex],
               name: editedStage.name,
               hours:
                  editedStage.hours?.value ||
                  updatedStagesData[stageIndex].hours,
               stations: editedStage.stations.map(station => ({
                  departmentId: station.value,
                  department: {
                     id: station.value,
                     departmentName: station.label,
                  },
               })),
            }
            setLocalStagesData(updatedStagesData)
         }
         toggleStages(stageId)
      }
   }
   const handleCancelStageSelection = stageId => {
      toggleStages(stageId)
      setEditedStages(prev => {
         const editStagesClone = { ...prev }
         delete editStagesClone[stageId]
         return editStagesClone
      })
   }
   const handleUpdateStage = async () => {
      if (selectedStages?.length) {
         toast.error(
            "You have selected stages to edit. Please save or cancel them before updating."
         )
         return
      }

      setIsLoading(true)
      try {
         const payload = {
            productionLineId: Number(productionLineId),
            stages: normalizeStageData(localStagesData, true),
         }

         const res = await AddProductionStages(payload)
         toast.success("Stages updated successfully!")
         handleModalClose()
         onSuccess()
      } catch (err) {
         console.error("Error updating stage:", err)
      } finally {
         setIsLoading(false)
      }
   }

   const initializeStageForEditing = stageId => {
      const stageData = localStagesData.find(stage => stage.id === stageId)

      const stationData =
         stageData.stations?.map(station => ({
            value: station.department?.id,
            label: station.department?.departmentName,
         })) || []

      const hoursData = {
         label: `${stageData.hours} Hrs`,
         value: stageData.hours,
      }

      if (stageData && !editedStages[stageId]) {
         setEditedStages(prev => ({
            ...prev,
            [stageId]: {
               name: stageData.name || "",
               stations: stationData,
               hours: hoursData,
            },
         }))
      }
   }

   const toggleStageSelection = stageId => {
      toggleStages(stageId)

      if (!hasSelectedStages(stageId)) {
         initializeStageForEditing(stageId)
      }
   }

   const handleDeleteStage = async stageId => {
      setIsDeleting(true)
      try {
         const filteredStagesData =
            localStagesData?.filter(stage => stage.id !== stageId) || []

         const payload = {
            productionLineId: Number(productionLineId),
            stages: normalizeStageData(filteredStagesData, true),
         }

         const res = await AddProductionStages(payload)
         toast.success("Stage deleted successfully!")
         setModal(prev => ({
            ...prev,
            isOpen: false,
         }))
         onSuccess()
      } catch (err) {
         console.error("Error deleting stage:", err)
      } finally {
         setIsDeleting(false)
      }
   }

   const handleOpenDeleteModal = stageId => {
      setSelection(stageId)
      setModal({
         isOpen: true,
         type: MODAL_TYPE.DELETE,
      })
   }
   const formConfig = [
      {
         title: COLUMNS.REORDER,
         className: "",
         render: () => {
            return (
               <div className="tooltip-container">
                  <img
                     src={reorderIcon}
                     alt="Reorder"
                     className="reorder-icon"
                  />
                  <span className="custom-tooltip">Drag</span>
               </div>
            )
         },
      },
      {
         title: COLUMNS.STAGES,
         className: "",
         render: data => {
            if (!hasSelectedStages(data.id)) {
               return data?.name || "-"
            }

            return (
               <TextInput
                  value={editedStages[data.id]?.name || ""}
                  onChange={e => handleStageNameChange(data.id, e.target.value)}
               />
            )
         },
      },
      {
         title: COLUMNS.STATIONS,
         className: "",
         render: data => {
            if (!hasSelectedStages(data.id)) {
               return formatStationsData(data)
            }

            return (
               <AutoComplete
                  isMulti
                  handleBlur={() => {
                     setStationsOption(prev => {
                        return {
                           ...prev,
                           page: 1,
                           Q: "",
                           data: [],
                        }
                     })
                     handleGetSelectEntityData(1)
                  }}
                  onChange={selectedOptions =>
                     handleStationsChange(data.id, selectedOptions)
                  }
                  onInputChange={val => {
                     handleSearchOnOptions(val)
                  }}
                  placeholder="Select department"
                  classNamePrefix="add-stage"
                  customStyles={AutoCompleteStyling}
                  options={departmentListing?.data}
                  value={editedStages[data.id]?.stations || []}
                  isLoading={departmentListing?.fetching}
                  handleScroll={() => {
                     if (departmentListing?.isDataAvailable) {
                        handleGetSelectEntityData(departmentListing?.page)
                     }
                  }}
                  menuPortalTarget={document.body}
               />
            )
         },
      },
      {
         title: COLUMNS.TIME,
         className: "",
         render: data => {
            if (!hasSelectedStages(data.id)) {
               return data?.hours || "-"
            }
            return (
               <AutoComplete
                  value={editedStages[data.id]?.hours || null}
                  isClearable={false}
                  onChange={selectedOption =>
                     handleHoursChange(data.id, selectedOption)
                  }
                  isSearchable={false}
                  placeholder="Select Hours"
                  customStyles={AutoCompleteStyling}
                  classNamePrefix="status-header-search-admin"
                  options={stageHoursSelectOptions}
                  menuPortalTarget={document.body}
               />
            )
         },
      },
      {
         title: COLUMNS.ACTIONS,
         render: data => (
            <div className="d-flex gap-3">
               <div className="w-45">
                  {!hasSelectedStages(data?.id) ? (
                     <div className="tooltip-container pointer">
                        <img
                           src={EditIcon}
                           alt="Edit"
                           onClick={() => toggleStageSelection(data.id)}
                        />
                        <span className="custom-tooltip">Edit</span>
                     </div>
                  ) : (
                     <div className="d-flex flex-column gap-2">
                        <div className="bg-primary bg-soft px-2 py-0.7 d-flex align-items-center justify-content-center rounded">
                           <span
                              className="text-primary cursor-pointer text-center"
                              onClick={() => handleSaveStage(data.id)}
                           >
                              Save
                           </span>
                        </div>
                        <div className=" bg-grey bg-soft px-2 py-0.7 d-flex align-items-center justify-content-center rounded">
                           <span
                              className="text-secondary cursor-pointer text-center"
                              onClick={() =>
                                 handleCancelStageSelection(data.id)
                              }
                           >
                              Cancel
                           </span>
                        </div>
                     </div>
                  )}
               </div>
               <div className="tooltip-container pointer">
                  <img
                     src={DeleteIcon}
                     alt="Delete"
                     onClick={() => handleOpenDeleteModal(data.id)}
                  />
                  <span className="custom-tooltip">Delete</span>
               </div>
            </div>
         ),
      },
   ]

   return (
      <Modal
         isOpen={isOpen}
         handleModalToggling={handleModalClose}
         customButton={true}
         hideModalHeaderSeparator={true}
         headerClasses="modal-header-container"
         backdrop="static"
         modalTitle={"Edit Stage"}
         size="xl"
         bodyClassName="px-3"
      >
         {selectedStages?.length ? (
            <Row className="px-3">
               <Col md={12}>
                  <DataTable
                     data={localStagesData}
                     className="table-responsive"
                     config={formConfig}
                  />
               </Col>
            </Row>
         ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
               <Droppable droppableId="droppable">
                  {provided => (
                     <div {...provided.droppableProps} ref={provided.innerRef}>
                        <Row className="px-3">
                           <Col md={12}>
                              <DataTable
                                 data={localStagesData}
                                 className="table-responsive"
                                 isDraggable={true}
                                 droppablePlaceholder={provided.placeholder}
                                 config={formConfig}
                              />
                           </Col>
                        </Row>
                     </div>
                  )}
               </Droppable>
            </DragDropContext>
         )}

         {!isEmpty(localStagesData) && (
            <div className="d-flex align-items-center justify-content-end py-3">
               <Button
                  className={`header-button headerButtonResponsive`}
                  title="Save"
                  onClick={handleUpdateStage}
                  isLoading={isLoading}
               />
            </div>
         )}

         <DeleteModal
            title={"Stage"}
            isOpen={modal.isOpen && modal.type === MODAL_TYPE.DELETE}
            toggleFunction={() => {
               setModal(prev => ({
                  ...prev,
                  isOpen: false,
               }))
            }}
            onDelete={() => handleDeleteStage(selectedStageId)}
            isLoading={isDeleting}
         />
      </Modal>
   )
}

export default EditStageModal
